import type { RestrictivePracticeStatus, RestrictivePracticeType } from '@piccolohealth/pbs-common';
import {
  ChakraV3,
  FloatingPopover,
  MultiSelect,
  type MultiSelectOption,
  NumberedBadge,
  type OnChangeRequest,
} from '@piccolohealth/ui';
import { P } from '@piccolohealth/util';
import React from 'react';
import { FaCaretDown, FaFilter } from 'react-icons/fa';

import { FormStack } from '../../../components/forms/FormStack';
import type { ParticipantRestrictivePracticesFilter } from '../hooks/useParticipantRestrictivePracticesFilter';
import { restrictivePracticeStatusOptions } from './RestrictivePracticeStatusTag';
import { restrictivePracticeTypeOptions } from './RestrictivePracticeTypeTag';

interface Props {
  filter: ParticipantRestrictivePracticesFilter;
}

const ParticipantRestrictivePracticeDescriptionFilterControl = (props: Props) => {
  const [value, setValue] = React.useState(props.filter.descriptionFilter);

  const onChange = React.useCallback(
    (value: string) => {
      setValue(value);
      props.filter.onDescriptionFilter(value);
    },
    [props.filter],
  );

  React.useEffect(() => {
    setValue(props.filter.descriptionFilter);
  }, [props.filter.descriptionFilter]);

  return (
    <ChakraV3.Input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder='All restrictive practices'
      size='md'
    />
  );
};

const RestrictivePracticeTypeFilterControl = (props: Props) => {
  const options: MultiSelectOption<RestrictivePracticeType>[] = Object.values(
    restrictivePracticeTypeOptions,
  );

  const selectedValues: MultiSelectOption<RestrictivePracticeType>[] = React.useMemo(() => {
    return (props.filter.typeFilter ?? []).flatMap((status) => {
      return options.find((o) => P.isEqual(o.raw, status)) ?? [];
    });
  }, [props.filter, options]);

  const onChange = React.useCallback(
    (req: OnChangeRequest<RestrictivePracticeType>) => {
      props.filter.onTypeFilter(req.values.map(({ raw }) => raw));
    },
    [props.filter],
  );

  return (
    <MultiSelect
      selectedValues={selectedValues}
      options={options}
      onChange={onChange}
      size='sm'
      placeholder='All types'
      optionVariant='tag'
    />
  );
};

const RestrictivePracticeStatusFilterControl = (props: Props) => {
  const options: MultiSelectOption<RestrictivePracticeStatus>[] = Object.values(
    restrictivePracticeStatusOptions,
  );

  const selectedValues: MultiSelectOption<RestrictivePracticeStatus>[] = React.useMemo(() => {
    return (props.filter.statusFilter ?? []).flatMap((status) => {
      return options.find((o) => P.isEqual(o.raw, status)) ?? [];
    });
  }, [props.filter, options]);

  const onChange = React.useCallback(
    (req: OnChangeRequest<RestrictivePracticeStatus>) => {
      props.filter.onStatusFilter(req.values.map(({ raw }) => raw));
    },
    [props.filter],
  );

  return (
    <MultiSelect
      selectedValues={selectedValues}
      options={options}
      onChange={onChange}
      size='sm'
      placeholder='All statuses'
      optionVariant='tag'
    />
  );
};

export const ParticipantRestrictivePracticeFilterControls = (props: Props) => {
  return (
    <FormStack orientation='horizontal'>
      <ChakraV3.Stack maxW='2xs'>
        <ParticipantRestrictivePracticeDescriptionFilterControl filter={props.filter} />
      </ChakraV3.Stack>
      <FloatingPopover
        render={() => (
          <ChakraV3.Stack bg='white' gap={0} w='xs' shadow='2xl' rounded='md' overflow='hidden'>
            <ChakraV3.HStack py={2} px={4} borderBottomWidth='1px'>
              <ChakraV3.Text fontWeight='semibold'>Filters</ChakraV3.Text>
              <ChakraV3.Spacer />
              <NumberedBadge count={props.filter.activeFilterCount} />
            </ChakraV3.HStack>
            <FormStack py={4} px={4} orientation='vertical'>
              <ChakraV3.FormField label='Type'>
                <RestrictivePracticeTypeFilterControl filter={props.filter} />
              </ChakraV3.FormField>
              <ChakraV3.FormField label='Status'>
                <RestrictivePracticeStatusFilterControl filter={props.filter} />
              </ChakraV3.FormField>
              <ChakraV3.Separator />

              <ChakraV3.Button
                onClick={props.filter.reset}
                w='fit-content'
                size='sm'
                variant='outline'
                disabled={props.filter.activeFilterCount === 0}
                colorPalette='brand'
              >
                Reset
              </ChakraV3.Button>
            </FormStack>
          </ChakraV3.Stack>
        )}
      >
        <ChakraV3.Button size='sm' variant='outline' lineHeight='unset'>
          <FaFilter />
          <ChakraV3.HStack>
            Filter
            <ChakraV3.Stack w='28px'>
              <NumberedBadge count={props.filter.activeFilterCount} />
            </ChakraV3.Stack>
          </ChakraV3.HStack>
          <FaCaretDown />
        </ChakraV3.Button>
      </FloatingPopover>
    </FormStack>
  );
};
